import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchDreamCards } from "../store/dashboardSlice";
import type { DreamCard } from "../store/dashboardSlice";
import { fetchSettings, saveCategories, MAX_CATEGORIES } from "../store/settingsSlice";
import { CategoryDrawer } from "../components/CategoryDrawer";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { useAuth0 } from "@auth0/auth0-react";
import { APP_ROUTES } from "../utils/appRoutes";
import { colors } from "../styles/colors";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui";
import { UpgradeModal } from "../components/upgrade";
import { useIsPremium } from "../hooks/useIsPremium";
import { FREE_PLAN_LIMITS, FREE_DEFAULT_CATEGORIES } from "../config/upgrade";

const COL_ORDER_KEY = "mydreams_col_order";

/* ── Status helpers ─────────────────────────────────────────────── */
type DreamStatus = "not-started" | "in-progress" | "completed";

function getDreamStatus(card: DreamCard): DreamStatus {
  if (card.totalMilestones === 0 && card.totalGoals === 0) return "not-started";
  if (card.completedMilestones === 0 && card.completedGoals === 0) return "not-started";
  if (
    card.totalMilestones > 0 && card.completedMilestones >= card.totalMilestones &&
    card.totalGoals > 0 && card.completedGoals >= card.totalGoals
  ) return "completed";
  if (card.totalMilestones > 0 && card.completedMilestones >= card.totalMilestones) return "completed";
  return "in-progress";
}

const STATUS_LABEL: Record<DreamStatus, string> = {
  "not-started": "Yet to start",
  "in-progress": "In progress",
  "completed": "Completed",
};

const STATUS_STYLE: Record<DreamStatus, { background: string; color: string }> = {
  "not-started": { background: "#F1F5F9", color: "#94A3B8" },
  "in-progress": { background: "#EFF6FF", color: "#3B82F6" },
  "completed":   { background: "#F0FDF4", color: "#22C55E" },
};

/* ── Dream cell ─────────────────────────────────────────────────── */
function DreamCell({
  card,
  onClick,
  onDragStart,
  onDragEnd,
  isDragging,
  canDrag,
}: {
  card: DreamCard;
  onClick: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
  canDrag: boolean;
}) {
  const status = getDreamStatus(card);
  const statusStyle = STATUS_STYLE[status];

  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => { e.stopPropagation(); onDragStart(); }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`group rounded-xl bg-white p-3 transition-shadow hover:shadow-md ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
      style={{
        border: `1px solid ${colors.borderLight}`,
        opacity: isDragging ? 0.4 : 1,
        transition: "opacity 0.15s",
      }}
    >
      <p className="m-0 text-[13px] font-semibold leading-snug text-slate-800 group-hover:text-brand-primary transition-colors">
        {card.title}
      </p>
      {card.description && (
        <p className="m-0 mt-1 line-clamp-1 text-[11px] leading-relaxed text-slate-400">
          {card.description}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="rounded-lg px-2 py-0.5 text-[10px] font-semibold" style={statusStyle}>
          {STATUS_LABEL[status]}
        </span>
        {card.totalMilestones > 0 && (
          <span className="text-[10px] text-slate-400">
            {card.completedMilestones}/{card.totalMilestones} milestones
          </span>
        )}
      </div>
      {card.totalMilestones > 0 && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${card.completedPercent}%`, backgroundColor: card.color }}
          />
        </div>
      )}
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────── */
const MyDreams = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { dreamCards, dreamCardsLoading } = useAppSelector((s) => s.dashboard);
  const isPremium = useIsPremium();
  const [showUpgradeGate, setShowUpgradeGate] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState<"dream_limit" | "category_limit">("dream_limit");

  const handleNewDream = () => {
    if (!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams) {
      setUpgradeTrigger("dream_limit");
      setShowUpgradeGate(true);
    } else {
      navigate(APP_ROUTES.dreamCreate);
    }
  };
  const settingsCategories = useAppSelector((s) => s.settings.categories);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());

  /* local dream cards so we can optimistically update category */
  const [localCards, setLocalCards] = useState<DreamCard[]>([]);
  useEffect(() => { setLocalCards(dreamCards); }, [dreamCards]);

  useEffect(() => {
    const token = () => getAccessTokenSilently({ cacheMode: "off" });
    dispatch(fetchDreamCards());
    dispatch(fetchSettings(token));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  /* ── Drag state ── */
  const draggingCardRef = useRef<{ card: DreamCard } | null>(null);
  const draggingColRef = useRef<string | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [draggingCol, setDraggingCol] = useState<string | null>(null);
  const [dropTargetCol, setDropTargetCol] = useState<string | null>(null);
  const [dropTargetColInsert, setDropTargetColInsert] = useState<string | null>(null);

  /* ── Column order (persisted in localStorage) ── */
  const [colOrder, setColOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(COL_ORDER_KEY) || "[]"); } catch { return []; }
  });

  /* ── Category drawer ── */
  const [catDrawer, setCatDrawer] = useState<{ open: boolean; mode: "add" | "edit"; name: string; color: string }>({
    open: false, mode: "add", name: "", color: "",
  });
  const closeCatDrawer = () => setCatDrawer((p) => ({ ...p, open: false }));

  /* ── Category menu / delete ── */
  const [catMenuOpen, setCatMenuOpen] = useState<string | null>(null);
  const [deletingCat, setDeletingCat] = useState<string | null>(null);
  useEffect(() => {
    if (!catMenuOpen) return;
    const close = () => setCatMenuOpen(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [catMenuOpen]);

  const handleDeleteCat = async (cat: string) => {
    const updated = settingsCategories.filter((c) => c.name !== cat);
    await dispatch(saveCategories({ categories: updated, getAccessToken: () => getAccessTokenSilently({ cacheMode: "off" }) }));
    const newOrder = colOrder.filter((c) => c !== cat);
    setColOrder(newOrder);
    localStorage.setItem(COL_ORDER_KEY, JSON.stringify(newOrder));
    setDeletingCat(null);
  };

  const handleCatDrawerSaved = (prevName?: string, newName?: string) => {
    if (prevName && newName && prevName !== newName) {
      const newOrder = colOrder.map((c) => c === prevName ? newName : c);
      setColOrder(newOrder);
      localStorage.setItem(COL_ORDER_KEY, JSON.stringify(newOrder));
    }
  };

  /* ── Filtered cards ── */
  const filteredCards = useMemo(
    () => localCards.filter((c) => {
      const sy = Number(c.startYear) || 0;
      const ty = Number(c.targetYear) || sy;
      return sy <= selectedYear && ty >= selectedYear;
    }),
    [localCards, selectedYear],
  );

  /* ── Build columns from settingsCategories + dreams ── */
  const { columns, categoryColors } = useMemo(() => {
    const colMap: Record<string, DreamCard[]> = {};
    const colorMap: Record<string, string> = {};
    /* seed all settings categories (even empty ones) */
    for (const cat of settingsCategories) {
      colMap[cat.name] = [];
      colorMap[cat.name] = cat.color;
    }
    /* populate with filtered dream cards */
    for (const card of filteredCards) {
      const cat = card.category || "Other";
      if (!colMap[cat]) { colMap[cat] = []; colorMap[cat] = card.color || colors.brandPrimary; }
      colMap[cat].push(card);
    }
    return { columns: colMap, categoryColors: colorMap };
  }, [filteredCards, settingsCategories]);

  /* ── Apply stored column order, append any new categories ── */
  const orderedCategoryNames = useMemo(() => {
    const all = Object.keys(columns);
    const known = colOrder.filter((c) => all.includes(c));
    const rest = all.filter((c) => !known.includes(c));
    return [...known, ...rest];
  }, [columns, colOrder]);

  /* ── Locked dreams for downgraded free users ── */
  const lockedDreamIds = useMemo(() => {
    if (isPremium || localCards.length <= FREE_PLAN_LIMITS.dreams) return new Set<string>();
    // First card (index 0) stays editable; the rest are read-only
    return new Set(localCards.filter((dream) => dream.freeEditable === false).map((dream) => dream.id));
  }, [isPremium, localCards]);
  const isDowngraded = !isPremium && lockedDreamIds.size > 0;


  /* ── Handlers: drag card ── */
  const handleCardDragStart = (card: DreamCard) => {
    if (!isPremium) { setUpgradeTrigger("category_limit"); setShowUpgradeGate(true); return; }
    draggingCardRef.current = { card };
    setDraggingCardId(card.id);
  };

  const handleCardDragEnd = () => {
    draggingCardRef.current = null;
    setDraggingCardId(null);
    setDropTargetCol(null);
  };

  const handleColDragOver = (e: React.DragEvent, cat: string) => {
    if (!isPremium || !draggingCardRef.current) return;
    e.preventDefault();
    setDropTargetCol(cat);
  };

  const handleColDrop = async (e: React.DragEvent, targetCat: string) => {
    e.preventDefault();
    if (!isPremium) return;
    const dragging = draggingCardRef.current;
    if (!dragging) return;
    const { card } = dragging;
    if (card.category === targetCat) { setDropTargetCol(null); return; }
    const newColor = categoryColors[targetCat] || card.color;
    /* optimistic update */
    setLocalCards((prev) =>
      prev.map((c) => c.id === card.id ? { ...c, category: targetCat, color: newColor } : c)
    );
    setDropTargetCol(null);
    draggingCardRef.current = null;
    setDraggingCardId(null);
    try {
      await request(API_URLS.dream(card.id), {
        method: "PUT",
        auth: true,
        getAccessToken: () => getAccessTokenSilently({ cacheMode: "off" }),
        body: { category: targetCat, color: newColor },
      });
    } catch {
      /* revert on failure */
      setLocalCards((prev) =>
        prev.map((c) => c.id === card.id ? { ...c, category: card.category, color: card.color } : c)
      );
    }
  };

  /* ── Handlers: drag column ── */
  const handleColHeaderDragStart = (e: React.DragEvent, cat: string) => {
    if (draggingCardRef.current) { e.preventDefault(); return; }
    draggingColRef.current = cat;
    setDraggingCol(cat);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleColHeaderDragOver = (e: React.DragEvent, cat: string) => {
    if (!draggingColRef.current || draggingColRef.current === cat) return;
    e.preventDefault();
    setDropTargetColInsert(cat);
  };

  const handleColHeaderDrop = (e: React.DragEvent, targetCat: string) => {
    e.preventDefault();
    const src = draggingColRef.current;
    if (!src || src === targetCat) { setDraggingCol(null); setDropTargetColInsert(null); draggingColRef.current = null; return; }
    const newOrder = [...orderedCategoryNames];
    const fromIdx = newOrder.indexOf(src);
    const toIdx = newOrder.indexOf(targetCat);
    if (fromIdx === -1 || toIdx === -1) return;
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, src);
    setColOrder(newOrder);
    localStorage.setItem(COL_ORDER_KEY, JSON.stringify(newOrder));
    setDraggingCol(null);
    setDropTargetColInsert(null);
    draggingColRef.current = null;
  };

  const handleColHeaderDragEnd = () => {
    draggingColRef.current = null;
    setDraggingCol(null);
    setDropTargetColInsert(null);
  };

  /* ── Year nav ── */
  const YearNav = (
    <div className="flex w-full items-center gap-1 rounded-xl bg-slate-50 p-1 sm:w-auto" style={{ border: `1px solid ${colors.borderLight}` }}>
      <button type="button" onClick={() => setSelectedYear((y) => y - 1)}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition-colors hover:bg-white hover:text-slate-700">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      <span className="flex-1 text-center text-[14px] font-bold sm:flex-none sm:min-w-[52px]" style={{ color: colors.textPrimary }}>{selectedYear}</span>
      <button type="button" onClick={() => setSelectedYear((y) => y + 1)}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 transition-colors hover:bg-white hover:text-slate-700">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2L10 7L5 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen space-y-4 py-4 md:space-y-6 md:py-6" style={{ backgroundColor: colors.pageSurface }} data-testid="my-dreams-page">
      <PageHeader
        title="My Dreams"
        subtitle="Dreams grouped by category"
        right={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto md:gap-3">
            {YearNav}
            {dreamCards.length > 0 && (
              <Button variant="primary" size="md" onClick={handleNewDream} className="hidden sm:flex">
                + New dream
              </Button>
            )}
          </div>
        }
      />

      {/* ── Loading ── */}
      {dreamCardsLoading && dreamCards.length === 0 && (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex w-52 shrink-0 flex-col gap-2">
              <div className="h-10 animate-pulse rounded-xl bg-slate-200" />
              {Array.from({ length: 3 }).map((__, j) => (
                <div key={j} className="h-20 animate-pulse rounded-xl bg-white" style={{ border: `1px solid ${colors.borderLight}` }} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Empty ── */}
      {!dreamCardsLoading && dreamCards.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl bg-white py-20 text-center shadow-sm" style={{ border: `1px solid ${colors.borderLight}` }}>
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="mb-4 opacity-25">
            <path d="M26 6 L30.5 18.5 L44 18.5 L33.5 26.5 L37.5 39 L26 31.5 L14.5 39 L18.5 26.5 L8 18.5 L21.5 18.5 Z" stroke="#64748B" strokeWidth="2.5" strokeLinejoin="round" fill="none"/>
            <circle cx="26" cy="6" r="2" fill="#64748B" opacity="0.5"/>
            <circle cx="44" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
            <circle cx="8" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
          </svg>
          <p className="m-0 text-[15px] font-semibold" style={{ color: colors.textPrimary }}>No dreams yet</p>
          <p className="m-0 mt-1 text-[13px]" style={{ color: colors.textTertiary }}>Create your first dream to start planning milestones</p>
          <Button variant="primary" size="md" className="mt-5" onClick={handleNewDream}>
            + New dream
          </Button>
        </div>
      )}

      {/* ── No dreams in selected year (but categories may exist) ── */}
      {!dreamCardsLoading && dreamCards.length > 0 && filteredCards.length === 0 && orderedCategoryNames.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
          <p className="text-base font-semibold text-slate-500">No dreams in {selectedYear}</p>
          <p className="mt-1 text-sm text-slate-400">Try a different year or create a new dream</p>
        </div>
      )}

      {/* ── Board ── */}
      {!dreamCardsLoading && (filteredCards.length > 0 || orderedCategoryNames.length > 0) && (
        <div>
          {/* Top bar: hints + Add category */}
          <div className="mb-4 flex flex-wrap items-center gap-3 md:gap-4">
            {isPremium ? (
              <span className="hidden items-center gap-1.5 text-[11px] text-slate-400 md:flex">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 15 11"/><line x1="12" y1="4" x2="12" y2="14"/><path d="M20 21H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2z"/>
                </svg>
                Drag cards between columns to change category
              </span>
            ) : (
              <button
                type="button"
                onClick={() => { setUpgradeTrigger("category_limit"); setShowUpgradeGate(true); }}
                className="hidden items-center gap-1.5 text-[11px] font-medium md:flex"
                style={{ background: "none", border: "none", cursor: "pointer", color: colors.brandPrimary, padding: 0 }}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <rect x="1" y="4" width="9" height="6.5" rx="1.2" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M3 4V2.8a2.5 2.5 0 1 1 5 0V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                Upgrade to Premium to drag dreams between categories
              </button>
            )}
            <span className="hidden text-slate-200 md:inline">|</span>
            <span className="hidden items-center gap-1.5 text-[11px] text-slate-400 md:flex">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
              Drag column headers to reorder
            </span>
            <div style={{ marginLeft: "auto" }}>
              <button
                type="button"
                onClick={() => {
                  const catLimit = isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories;
                  if (settingsCategories.length >= catLimit) {
                    if (!isPremium) { setUpgradeTrigger("category_limit"); setShowUpgradeGate(true); }
                    return;
                  }
                  setCatDrawer({ open: true, mode: "add", name: "", color: "" });
                }}
                disabled={isPremium && settingsCategories.length >= MAX_CATEGORIES}
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-[12px] font-semibold transition-colors"
                style={{
                  background: "rgba(0,136,255,0.06)",
                  color: settingsCategories.length >= (isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories) ? "#CBD5E1" : colors.brandPrimary,
                  border: `1.5px dashed ${settingsCategories.length >= (isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories) ? "#E2E8F0" : `${colors.brandPrimary}60`}`,
                  cursor: settingsCategories.length >= (isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories) ? "not-allowed" : "pointer",
                  lineHeight: "1.25rem",
                  boxSizing: "border-box",
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {settingsCategories.length >= (isPremium ? MAX_CATEGORIES : FREE_PLAN_LIMITS.categories)
                  ? (isPremium ? `Max ${MAX_CATEGORIES} categories` : `${FREE_PLAN_LIMITS.categories} max (free)`)
                  : "Add category"}
              </button>
            </div>
          </div>

          {/* Wrapping grid — responsive: 1 col mobile, 2 col sm, auto-fill lg+ */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 208px), 1fr))", gap: "0.75rem", alignItems: "start" }}>

            {orderedCategoryNames.map((cat, catIndex) => {
              const catColor = categoryColors[cat];
              const dreams = columns[cat] || [];
              const isDropTarget = dropTargetCol === cat;
              const isColDragging = draggingCol === cat;
              const isColInsertTarget = dropTargetColInsert === cat;
              const isCatLocked = !isPremium && catIndex >= FREE_PLAN_LIMITS.categories;

              return (
                <div
                  key={cat}
                  className="flex min-w-0 flex-col gap-2"
                  style={{ opacity: isColDragging ? 0.45 : 1, transition: "opacity 0.15s" }}
                  onDragOver={(e) => handleColDragOver(e, cat)}
                  onDrop={(e) => handleColDrop(e, cat)}
                >
                  {/* Column header — draggable for reorder */}
                  <div style={{ position: "relative" }}>
                    <div
                      draggable
                      onDragStart={(e) => handleColHeaderDragStart(e, cat)}
                      onDragOver={(e) => handleColHeaderDragOver(e, cat)}
                      onDrop={(e) => handleColHeaderDrop(e, cat)}
                      onDragEnd={handleColHeaderDragEnd}
                      className="flex items-center justify-between rounded-xl px-3 py-2.5"
                      style={{
                        background: isCatLocked ? "#9CA3AF" : catColor,
                        minHeight: 44,
                        cursor: "grab",
                        outline: isColInsertTarget ? "2px dashed rgba(255,255,255,0.7)" : "none",
                        outlineOffset: 2,
                      }}
                    >
                      <span className="text-[12px] font-bold uppercase tracking-wide text-white flex items-center gap-1.5">
                        {isCatLocked && (
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" className="opacity-80 flex-shrink-0">
                            <rect x="1" y="4" width="9" height="6.5" rx="1.2" stroke="white" strokeWidth="1.2"/>
                            <path d="M3 4V2.8a2.5 2.5 0 1 1 5 0V4" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
                          </svg>
                        )}
                        {cat}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold text-white">{dreams.length}</span>
                        {/* Menu trigger */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCatMenuOpen(catMenuOpen === cat ? null : cat); }}
                          style={{
                            appearance: "none", border: "none", background: "rgba(255,255,255,0.2)",
                            borderRadius: 6, width: 24, height: 24, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff", flexShrink: 0, padding: 0,
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Dropdown menu */}
                    {catMenuOpen === cat && (
                      <div
                        style={{
                          position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50,
                          background: "#fff", borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          border: "1px solid rgba(0,0,0,0.08)", minWidth: 140, overflow: "hidden",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => { const found = settingsCategories.find((c) => c.name === cat); setCatDrawer({ open: true, mode: "edit", name: cat, color: found?.color || "" }); setCatMenuOpen(null); }}
                          style={{
                            width: "100%", padding: "9px 14px", border: "none", background: "none",
                            textAlign: "left", fontSize: 13, cursor: "pointer", display: "flex",
                            alignItems: "center", gap: 8, color: "#0b1b3a",
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          Edit category
                        </button>
                        <div style={{ height: 1, background: "rgba(0,0,0,0.06)", margin: "0 10px" }} />
                        <button
                          type="button"
                          onClick={() => { setDeletingCat(cat); setCatMenuOpen(null); }}
                          style={{
                            width: "100%", padding: "9px 14px", border: "none", background: "none",
                            textAlign: "left", fontSize: 13, cursor: "pointer", display: "flex",
                            alignItems: "center", gap: 8, color: "#dc2626",
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                          Delete category
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Delete confirmation / blocked */}
                  {deletingCat === cat && (
                    <div
                      className="flex flex-col gap-3 rounded-xl p-3"
                      style={{
                        background: dreams.length > 0 ? "#FFF7ED" : "#FFF5F5",
                        border: `1px solid ${dreams.length > 0 ? "#FED7AA" : "#FECACA"}`,
                      }}
                    >
                      {dreams.length > 0 ? (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#C2410C" }}>Cannot delete</div>
                          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.55)", lineHeight: 1.5 }}>
                            This category has {dreams.length} dream{dreams.length > 1 ? "s" : ""}. Move them to another category first, then delete.
                          </div>
                          <button type="button" onClick={() => setDeletingCat(null)}
                            style={{ border: `1px solid ${colors.borderLight}`, borderRadius: 8, background: "#fff", fontSize: 12, padding: "5px 0", cursor: "pointer", fontWeight: 600 }}>
                            OK
                          </button>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#DC2626" }}>Delete "{cat}"?</div>
                          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)", lineHeight: 1.5 }}>This cannot be undone.</div>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setDeletingCat(null)}
                              style={{ flex: 1, border: `1px solid ${colors.borderLight}`, borderRadius: 8, background: "#fff", fontSize: 12, padding: "5px 0", cursor: "pointer" }}>
                              Cancel
                            </button>
                            <button type="button" onClick={() => handleDeleteCat(cat)}
                              style={{ flex: 1, border: "none", borderRadius: 8, background: "#DC2626", color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 0", cursor: "pointer" }}>
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Drop zone highlight */}
                  <div
                    className="flex flex-col gap-2 rounded-xl transition-all"
                    style={{
                      minHeight: 40,
                      padding: isDropTarget ? "6px" : "0px",
                      background: isCatLocked ? "rgba(156,163,175,0.08)" : isDropTarget ? `${catColor}18` : "transparent",
                      border: isCatLocked ? "2px dashed #D1D5DB" : isDropTarget ? `2px dashed ${catColor}80` : "2px solid transparent",
                      borderRadius: 12,
                    }}
                  >
                    {dreams.map((card) => {
                      const isDreamLocked = lockedDreamIds.has(card.id);
                      return (
                        <div key={card.id} style={{ position: "relative" }}>
                          <DreamCell
                            card={card}
                            isDragging={draggingCardId === card.id}
                            canDrag={isPremium}
                            onDragStart={() => handleCardDragStart(card)}
                            onDragEnd={handleCardDragEnd}
                            onClick={() => {
                              if (isDreamLocked) { setUpgradeTrigger("dream_limit"); setShowUpgradeGate(true); return; }
                              navigate(APP_ROUTES.dreamDetail(card.id));
                            }}
                          />
                          {isDreamLocked && (
                            <div
                              className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl"
                              style={{ background: "rgba(248,250,252,0.82)", backdropFilter: "blur(2px)", border: "1px solid #E2E8F0" }}
                              onClick={() => { setUpgradeTrigger("dream_limit"); setShowUpgradeGate(true); }}
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <rect x="2" y="6.5" width="12" height="8" rx="1.5" stroke="#94A3B8" strokeWidth="1.3"/>
                                <path d="M5 6.5V4.5a3 3 0 1 1 6 0v2" stroke="#94A3B8" strokeWidth="1.3" strokeLinecap="round"/>
                              </svg>
                              <span className="text-[10px] font-semibold" style={{ color: "#94A3B8" }}>Premium only</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {dreams.length === 0 && !isDropTarget && (
                      <div className="rounded-xl border border-dashed border-slate-200 py-5 text-center text-[11px] text-slate-300">
                        Drop a dream here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      )}

      {/* ── Category Drawer ── */}
      <CategoryDrawer
        open={catDrawer.open}
        mode={catDrawer.mode}
        initialName={catDrawer.name}
        initialColor={catDrawer.color || undefined}
        onClose={closeCatDrawer}
        onSaved={() => handleCatDrawerSaved(
          catDrawer.mode === "edit" ? catDrawer.name : undefined,
          undefined
        )}
      />

      {/* ── Locked 'Add another dream' card — shown to free users at limit ── */}
      {!isPremium && dreamCards.length >= FREE_PLAN_LIMITS.dreams && !dreamCardsLoading && (
        <button
          type="button"
          onClick={() => setShowUpgradeGate(true)}
          className="flex w-full cursor-pointer items-center gap-4 rounded-2xl px-5 py-4 transition-colors hover:opacity-90"
          style={{
            background: `linear-gradient(90deg, ${colors.brandPrimary}10 0%, ${colors.brandPrimary}06 100%)`,
            border: `1.5px dashed ${colors.brandPrimary}40`,
          }}
        >
          <span
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl"
            style={{ background: `${colors.brandPrimary}15`, color: colors.brandPrimary }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="7" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M5 7V5a3 3 0 1 1 6 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </span>
          <div className="text-left">
            <p className="m-0 text-[13px] font-semibold" style={{ color: colors.brandPrimary }}>
              Add another dream
            </p>
            <p className="m-0 text-[12px]" style={{ color: colors.textTertiary }}>
              Upgrade to Premium to create unlimited dreams
            </p>
          </div>
          <span
            className="ml-auto rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
            style={{ background: colors.brandPrimary }}
          >
            Premium
          </span>
        </button>
      )}

      <UpgradeModal
        open={showUpgradeGate}
        trigger={upgradeTrigger}
        onClose={() => setShowUpgradeGate(false)}
      />
    </div>
  );
};

export default MyDreams;
